import { faHotel, faNewspaper, faPlane, faStar } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React, { createContext, ReactNode, useContext } from 'react'
import { useIntl } from 'react-intl'
import { BookerContext } from '../Booker'
import { BedIcon } from '../Booker/components/HotelSelector'
import { cn, cnWithPrefix } from 'ui/lib/utils'

const NavBarContext = createContext<NavBarType>({} as NavBarType)

export type service = 'hotel' | 'hotelFlight' | 'service' | null

type NavBarType = {
   selected: service
   onChange?: Function
   selectedClassName?: string
   useTailwindPrefix?: boolean
}

type Props = NavBarType & {
   children: ReactNode
   className?: string
}

type ItemProps = {
   children: ReactNode
   value?: service
   className?: string
   innerClassName?: string
   url?: string
   icon?: string
}

const PlaneIcon = ({ className }: { className?: string }) => {
   const { useTailwindPrefix } = useContext(NavBarContext)
   const customCn = useTailwindPrefix ? cnWithPrefix('tw-') : cn
   return (
      <svg
         className={customCn(
            {
               'h-[1em] overflow-visible box-content': !useTailwindPrefix,
               'tw-h-[1em] tw-overflow-visible tw-box-content': useTailwindPrefix,
            },
            className,
         )}
         xmlns="http://www.w3.org/2000/svg"
         viewBox="0 -960 960 960"
         fill="currentColor"
      >
         <path d="M120-120v-80h720v80H120Zm70-200L40-570l96-26 112 94 140-37-207-276 116-31 299 251 170-46q32-9 60.5 7.5T864-585q9 32-7.5 60.5T808-487L190-320Z" />
      </svg>
   )
}

const Item = ({ children, value, className, url, icon, innerClassName }: ItemProps) => {
   const { onChange, selected, selectedClassName, useTailwindPrefix } = useContext(NavBarContext)
   const { version } = useContext(BookerContext)
   const onClick = () => {
      onChange && onChange(value)
   }
   const customCn = useTailwindPrefix ? cnWithPrefix('tw-') : cn
   return (
      <li
         className={classNames(className, selectedClassName && selected === value && `${selectedClassName}`, {
            'relative block': !useTailwindPrefix,
            'tw-relative tw-block': useTailwindPrefix,
         })}
      >
         <a
            className={customCn(
               'itm-booker-navItem',
               {
                  'py-2 px-4 md:px-8 rounded-full block whitespace-nowrap truncate w-full':
                     version === 'padded' && !useTailwindPrefix,
                  'tw-py-2 tw-px-4 md:tw-px-8 tw-rounded-full tw-block tw-whitespace-nowrap tw-truncate tw-w-full':
                     version === 'padded' && useTailwindPrefix,
                  'bg-primary-booker text-primary-booker-a11y':
                     version === 'padded' && selected === value && !useTailwindPrefix,
                  'tw-bg-primary-booker tw-text-primary-booker-a11y':
                     version === 'padded' && selected === value && useTailwindPrefix,
                  'bg-widget-pill-background text-widget-pill-a11y cursor-pointer':
                     version === 'padded' && selected !== value && !useTailwindPrefix,
                  'tw-bg-widget-pill-background tw-text-widget-pill-a11y tw-cursor-pointer':
                     version === 'padded' && selected !== value && useTailwindPrefix,
                  'relative block py-2 px-6 border-b-[3px] whitespace-nowrap truncate':
                     version !== 'padded' && !useTailwindPrefix,
                  'tw-relative tw-block tw-py-2 tw-px-6 tw-border-b-[3px] tw-whitespace-nowrap tw-truncate':
                     version !== 'padded' && useTailwindPrefix,
                  'border-gray-300': selected === value && !useTailwindPrefix,
                  'tw-border-gray-300': selected === value && useTailwindPrefix,
                  'border-transparent cursor-pointer': selected !== value && !useTailwindPrefix,
                  'tw-border-transparent tw-cursor-pointer': selected !== value && useTailwindPrefix,
               },
               innerClassName,
            )}
            {...(url ? { href: url, target: '_blank', rel: 'nofollow' } : { onClick: onClick })}
         >
            {icon &&
               (icon.indexOf('plane') >= 0 ? (
                  <>
                     <FontAwesomeIcon icon={faPlane} rotation={270} />
                     &nbsp;
                  </>
               ) : icon.indexOf('newspaper') >= 0 ? (
                  <>
                     <FontAwesomeIcon icon={faNewspaper} />
                     &nbsp;
                  </>
               ) : null)}
            {children}
         </a>
      </li>
   )
}

const HotelItem = ({ isClear }: { isClear?: boolean }) => {
   const intl = useIntl()
   const { version, useTailwindPrefix } = useContext(BookerContext)
   return (
      <Item
         value="hotel"
         innerClassName={classNames({
            'bg-widget-pill-background text-transparent': version === 'padded' && isClear && !useTailwindPrefix,
            'tw-bg-widget-pill-background tw-text-transparent': version === 'padded' && isClear && useTailwindPrefix,
         })}
      >
         {version === 'padded' ? (
            <BedIcon
               className={classNames({
                  'inline-block align-[-0.125rem]': !useTailwindPrefix,
                  'tw-inline-block tw-align-[-0.125rem]': useTailwindPrefix,
               })}
            />
         ) : (
            <FontAwesomeIcon icon={faHotel} />
         )}
         &nbsp;
         {intl.formatMessage({ id: 'booker.hotel' })}
      </Item>
   )
}

const HotelFlightItem = () => {
   const intl = useIntl()
   const { version, useTailwindPrefix } = useContext(BookerContext)
   return (
      <Item value="hotelFlight">
         {version === 'padded' ? (
            <PlaneIcon
               className={classNames({
                  'inline-block align-[-0.125rem]': !useTailwindPrefix,
                  'tw-inline-block tw-align-[-0.125rem]': useTailwindPrefix,
               })}
            />
         ) : (
            <FontAwesomeIcon icon={faPlane} rotation={270} />
         )}
         &nbsp;
         {intl.formatMessage({ id: 'booker.hotel' })}&nbsp;+&nbsp;
         {intl.formatMessage({ id: 'booker.flight' })}
      </Item>
   )
}

const ServiceItem = () => {
   const intl = useIntl()
   return (
      <Item value="service">
         <FontAwesomeIcon icon={faStar} />
         &nbsp;
         {intl.formatMessage({ id: 'booker.services' })}
      </Item>
   )
}

export const NavBar = ({ children, onChange, selected, className, selectedClassName, useTailwindPrefix }: Props) => {
   const { version } = useContext(BookerContext)
   return (
      <NavBarContext.Provider value={{ onChange, selected, selectedClassName, useTailwindPrefix }}>
         <ul
            className={classNames(className, {
               'space-x-4': version === 'padded' && !useTailwindPrefix,
               'tw-space-x-4': version === 'padded' && useTailwindPrefix,
               'mb-3.5 bg-transparent flex': !useTailwindPrefix,
               'tw-mb-3.5 tw-bg-transparent tw-flex': useTailwindPrefix,
            })}
         >
            {children}
         </ul>
      </NavBarContext.Provider>
   )
}
NavBar.Item = Item
NavBar.HotelItem = HotelItem
NavBar.HotelFlightItem = HotelFlightItem
NavBar.ServiceItem = ServiceItem
